import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider, keepPreviousData } from '@tanstack/react-query';
import { GoogleOAuthProvider } from '@react-oauth/google';

import { AuthProvider } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import { ToasterProvider } from './components/Toaster';

import Login from './pages/Login';
// Loaded on first visit, not up front (DATA-340); Layout shows a placeholder meanwhile.
import {
  Dashboard, Sources, Destinations, Pipelines, Runs, Settings, Notifications, Exports,
} from './pages/pageLoaders';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30000,
      // PF-04 (docs/PERFORMANCE.md). Without this, React Query drops the
      // rendered rows and falls back to `isLoading` on every refetch — so
      // each 15s poll and each navigation past `staleTime` blanked the table
      // and showed a spinner for a full round trip. Users are ~306ms RTT
      // from the cluster, which made a ~1.5s request present as ~1.5s of
      // empty screen. Keeping the previous result on screen and swapping it
      // when the new one lands is the cheapest real win available: the data
      // was already in hand.
      //
      // Pages that want to show a refresh is in flight have `isFetching`;
      // adopt it per-page rather than reintroducing the blanking.
      //
      // NOTE: this keeps previous data across a *queryKey change* too, which
      // is wrong wherever the key encodes a selection — you would render one
      // entity's data under another's heading. `['run', selectedRunId]` in
      // pages/Runs.tsx opts out for exactly that reason. Check the key before
      // relying on this default in a new query.
      placeholderData: keepPreviousData,
    },
  },
});

const CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';

const App: React.FC = () => {
  return (
    <GoogleOAuthProvider clientId={CLIENT_ID}>
      <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <ToasterProvider>
          <AuthProvider>
            <BrowserRouter>
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Navigate to="/dashboard" replace />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Dashboard />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/sources"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Sources />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/destinations"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Destinations />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/pipelines"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Pipelines />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/runs"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Runs />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/notifications"
                element={
                  <ProtectedRoute>
                    <Layout>
                      <Notifications />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/exports"
                element={
                  <ProtectedRoute requireAdmin>
                    <Layout>
                      <Exports />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings/*"
                element={
                  <ProtectedRoute requireAdmin>
                    <Layout>
                      <Settings />
                    </Layout>
                  </ProtectedRoute>
                }
              />
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </BrowserRouter>
          </AuthProvider>
        </ToasterProvider>
      </QueryClientProvider>
      </ThemeProvider>
    </GoogleOAuthProvider>
  );
};

export default App;